document.documentElement.classList.remove("no-js");

class OralConfigurator extends HTMLElement {
  connectedCallback() {
    delete this.dataset.oralEnhanced;
    this.radios = Array.from(this.querySelectorAll('input[name^="oral-edition-"]'));
    this.visuals = Array.from(this.querySelectorAll("[data-oral-visual][data-edition]"));
    this.panels = Array.from(this.querySelectorAll("[data-edition-panel]"));
    this.buttons = Array.from(this.querySelectorAll("[data-edition-select], [data-priority-control]"));
    this.summary = this.querySelector("[data-selected-summary]");
    this.onButtonClick = this.onButtonClick.bind(this);
    this.onRadioChange = this.onRadioChange.bind(this);
    this.buttons.forEach((button) => button.addEventListener("click", this.onButtonClick));
    this.radios.forEach((radio) => radio.addEventListener("change", this.onRadioChange));
    if (this.dataset.activeEdition === "silver" || this.dataset.activeEdition === "gold") {
      this.setActiveEdition(this.dataset.activeEdition);
    } else if (this.summary) {
      this.summary.textContent = "Choose Silver or Gold to continue.";
    }
    this.dataset.oralEnhanced = "true";
  }

  disconnectedCallback() {
    this.buttons?.forEach((button) => button.removeEventListener("click", this.onButtonClick));
    this.radios?.forEach((radio) => radio.removeEventListener("change", this.onRadioChange));
    delete this.dataset.oralEnhanced;
  }

  onButtonClick(event) {
    const edition = event.currentTarget.dataset.editionSelect || event.currentTarget.dataset.edition;
    this.setActiveEdition(edition);
  }

  onRadioChange(event) {
    this.setActiveEdition(event.currentTarget.value);
  }

  setActiveEdition(edition) {
    if (edition !== "silver" && edition !== "gold") return;
    this.dataset.activeEdition = edition;
    this.visuals.forEach((visual) => {
      visual.dataset.active = String(visual.dataset.edition === edition);
    });
    this.buttons.forEach((button) => {
      const buttonEdition = button.dataset.editionSelect || button.dataset.edition;
      button.setAttribute("aria-pressed", String(buttonEdition === edition));
    });
    this.radios.forEach((radio) => {
      radio.checked = radio.value === edition;
    });
    this.panels.forEach((panel) => {
      panel.dataset.active = String(panel.dataset.editionPanel === edition);
    });
    if (this.summary) {
      this.summary.textContent = `${edition === "gold" ? "Gold" : "Silver"} Edition selected.`;
      this.summary.setAttribute("aria-disabled", "false");
    }
  }
}

if (!customElements.get("oral-configurator")) customElements.define("oral-configurator", OralConfigurator);

class OralPairOffer extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector("[data-pair-add]");
    this.status = this.querySelector("[data-pair-status]");
    this.cartLink = this.querySelector("[data-pair-cart]");
    if (!this.button || !this.status || !this.cartLink || typeof window.fetch !== "function") return;
    if (!/^\d+$/.test(this.dataset.silverVariant) || !/^\d+$/.test(this.dataset.goldVariant)) return;
    if (this.dataset.silverVariant === this.dataset.goldVariant) return;
    this.onAddPair = this.onAddPair.bind(this);
    this.button.addEventListener("click", this.onAddPair);
    this.button.hidden = false;
    this.querySelector("[data-pair-fallback]")?.setAttribute("hidden", "");
  }

  disconnectedCallback() {
    this.button?.removeEventListener("click", this.onAddPair);
  }

  async onAddPair() {
    if (this.button.disabled) return;
    this.button.disabled = true;
    this.status.textContent = "Adding one Silver and one Gold…";
    let added = false;
    try {
      const response = await withRequestDeadline((signal) => fetch(this.dataset.cartAddUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        signal,
        body: JSON.stringify({ items: [
          { id: this.dataset.silverVariant, quantity: 1 },
          { id: this.dataset.goldVariant, quantity: 1 },
        ] }),
      }), 8000);
      added = response.ok;
    } catch {
      added = false;
    }

    // A failed request can still add available stock. Read back; never retry or clear.
    let cart;
    try {
      cart = await withRequestDeadline(async (signal) => {
        const response = await fetch(this.dataset.cartReadUrl, {
          credentials: "same-origin", cache: "no-store", signal, headers: { Accept: "application/json" },
        });
        return response.ok ? response.json() : null;
      }, 5000);
    } catch {
      cart = null;
    }
    if (!added || !cart) {
      this.status.textContent = "Check your bag before trying again. We could not confirm that both items were added.";
      this.button.disabled = false;
    } else if (this.hasPairPrice(cart)) {
      this.status.textContent = "Pair added. View your bag to see the applied offer.";
    } else {
      this.status.textContent = "Items added, but the pair offer could not be confirmed. Check your bag before continuing.";
    }
    if (added && cart && Number.isInteger(cart.item_count) && cart.item_count >= 0) {
      document.querySelectorAll("[data-cart-count]").forEach((count) => { count.textContent = String(cart.item_count); });
    }
    this.cartLink.hidden = false;
  }

  hasPairPrice(cart) {
    if (cart.currency !== "USD" || !Array.isArray(cart.items)) return false;
    let silverQuantity = 0;
    let goldQuantity = 0;
    let total = 0;
    for (const item of cart.items) {
      const id = String(item.variant_id);
      if (id !== this.dataset.silverVariant && id !== this.dataset.goldVariant) continue;
      if (!Number.isInteger(item.quantity) || item.quantity < 1 || !Number.isInteger(item.final_line_price)) return false;
      if (id === this.dataset.silverVariant) silverQuantity += item.quantity;
      else goldQuantity += item.quantity;
      total += item.final_line_price;
    }
    const pairCount = Math.min(silverQuantity, goldQuantity);
    return pairCount > 0 && total === (silverQuantity + goldQuantity) * 1699 - pairCount * 599;
  }
}

if (!customElements.get("oral-pair-offer")) customElements.define("oral-pair-offer", OralPairOffer);

class ProductPurchase extends HTMLElement {
  connectedCallback() {
    this.pdpSelect = this.querySelector("[data-pdp-variant-select]");
    if (this.pdpSelect) {
      this.purchaseForm = this.querySelector('form[method="post"]');
      if (this.purchaseForm) {
        this.productSection = this.closest(".product-main");
        this.mediaRegion = this.productSection?.querySelector(".product-main__media");
        this.claims = this.querySelectorAll("[data-variant-claims]");
        this.nailOffer = this.querySelector("[data-nail-pdp-offer]");
        const next = this.productSection?.nextElementSibling;
        const structuredData = next?.matches("[data-nail-pdp-guide]") ? next.nextElementSibling : next;
        this.structuredData = structuredData?.matches("[data-product-json-ld]") ? structuredData : null;
        this.onPdpVariantChange = this.onPdpVariantChange.bind(this);
        this.onPdpSubmit = this.onPdpSubmit.bind(this);
        this.pdpSelect.addEventListener("change", this.onPdpVariantChange);
        this.purchaseForm.addEventListener("submit", this.onPdpSubmit);
        this.dataset.pdpEnhanced = "true";
      }
      return;
    }
    this.select = this.querySelector("[data-variant-select]");
    if (!this.select) return;
    this.form = this.select.closest("form");
    this.price = this.querySelector("[data-product-price]");
    this.button = this.querySelector("[data-add-button]");
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.select.addEventListener("change", this.onVariantChange);
    this.form?.addEventListener("submit", this.onSubmit);
  }

  disconnectedCallback() {
    this.pdpSelect?.removeEventListener("change", this.onPdpVariantChange);
    this.purchaseForm?.removeEventListener("submit", this.onPdpSubmit);
    this.select?.removeEventListener("change", this.onVariantChange);
    this.form?.removeEventListener("submit", this.onSubmit);
  }

  onPdpVariantChange() {
    const option = this.pdpSelect.selectedOptions[0];
    if (!option || !this.purchaseForm) return;
    const eligible = option.dataset.orderEligible === "true";
    const id = this.purchaseForm.querySelector('input[name="id"]');
    const quantity = this.purchaseForm.querySelector('input[name="quantity"]');
    const button = this.purchaseForm.querySelector("[data-add-button]");
    const price = this.querySelector("[data-product-price]");
    const compare = this.querySelector("[data-product-compare]");
    const selected = this.querySelector("[data-selected-variant]");
    if (id) id.value = eligible ? option.value : "";
    this.pdpSelect.dataset.commerceVariantId = eligible ? option.value : "";
    if (price) price.textContent = eligible ? option.dataset.price : "Unavailable";
    if (compare) {
      compare.hidden = !eligible || !option.dataset.compare;
      compare.textContent = eligible && option.dataset.compare ? `Previously ${option.dataset.compare}` : "";
    }
    if (quantity) {
      quantity.disabled = !eligible;
      const min = option.dataset.quantityMin || "1";
      quantity.min = min;
      quantity.max = option.dataset.quantityMax || "";
      quantity.step = option.dataset.quantityStep || "1";
      quantity.value = min;
    }
    if (button) {
      button.disabled = !eligible || this.purchaseForm.dataset.addPending === "true";
      button.textContent = eligible ? button.dataset.addLabel : button.dataset.soldLabel;
      button.dataset.commerceVariantId = eligible ? option.value : "";
    }
    if (this.productSection) this.productSection.dataset.commerceVariantId = eligible ? option.value : "";
    if (this.mediaRegion) this.mediaRegion.hidden = false;
    this.claims?.forEach((claim) => { claim.hidden = false; });
    if (this.nailOffer) this.nailOffer.hidden = !eligible;
    if (!eligible) this.structuredData?.remove();
    else if (this.structuredData && !this.structuredData.isConnected) this.productSection?.after(this.structuredData);
    if (selected) selected.textContent = `Selected: ${option.dataset.variantTitle}`;
    const url = new URL(window.location.href);
    url.searchParams.set("variant", option.value);
    window.history.replaceState({}, "", url);
    this.mediaRegion?.querySelectorAll("[data-media-id]").forEach((media) => {
      media.dataset.activeMedia = String(media.dataset.mediaId === option.dataset.variantMediaId);
    });
  }

  onPdpSubmit(event) {
    const option = this.pdpSelect?.selectedOptions[0];
    const id = this.purchaseForm?.querySelector('input[name="id"]');
    if (option?.dataset.orderEligible !== "true" || !/^\d+$/.test(option.value) || id?.value !== option.value) {
      event.preventDefault();
    }
  }

  onSubmit(event) {
    if (this.select.selectedOptions[0]?.dataset.orderEligible !== "true" || !this.form?.checkValidity()) event.preventDefault();
  }

  onVariantChange() {
    const option = this.select.selectedOptions[0];
    if (!option) return;
    const eligible = option.dataset.orderEligible === "true";
    if (this.price) this.price.textContent = eligible && option.dataset.price ? option.dataset.price : "Unavailable";
    if (this.button) {
      this.button.disabled = !eligible;
      this.button.textContent = eligible ? this.button.dataset.addLabel : "Unavailable";
    }
    const quantity = this.form?.querySelector('input[name="quantity"]');
    if (quantity) {
      quantity.min = option.dataset.quantityMin || "1";
      quantity.max = option.dataset.quantityMax || "";
      quantity.step = option.dataset.quantityStep || "1";
      quantity.value = quantity.min;
    }
    const url = new URL(window.location.href);
    url.searchParams.set("variant", option.value);
    window.history.replaceState({}, "", url);
    const media = option.dataset.mediaId
      ? document.querySelector(`[data-media-id="${option.dataset.mediaId}"]`)
      : null;
    media?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }
}

if (!customElements.get("product-purchase")) customElements.define("product-purchase", ProductPurchase);

function withRequestDeadline(operation, milliseconds) {
  const controller = new AbortController();
  let timer;
  const deadline = new Promise((_, reject) => {
    timer = window.setTimeout(() => {
      controller.abort();
      reject(new Error("Request outcome uncertain"));
    }, milliseconds);
  });
  let request;
  try {
    request = operation(controller.signal);
  } catch (error) {
    window.clearTimeout(timer);
    return Promise.reject(error);
  }
  return Promise.race([request, deadline]).finally(() => window.clearTimeout(timer));
}

document.addEventListener("submit", async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  const region = form.matches("[data-ajax-add-form]") ? form : form.querySelector("[data-ajax-add-form]");
  if (!region || typeof window.fetch !== "function") return;
  event.preventDefault();
  if (form.dataset.addPending === "true") return;
  const id = form.querySelector('input[name="id"]')?.value;
  const quantity = form.querySelector('input[name="quantity"]');
  if (!/^\d+$/.test(id || "") || (quantity && !quantity.checkValidity())) return;
  const button = region.querySelector("[data-add-button]");
  const status = region.querySelector("[data-add-status]");
  const cartLink = region.querySelector("[data-add-cart-link]");
  form.dataset.addPending = "true";
  if (button) button.disabled = true;
  if (status) status.textContent = "Adding to bag…";
  try {
    const added = await withRequestDeadline(async (signal) => {
      const response = await window.fetch(region.dataset.cartAddUrl, {
        method: "POST", credentials: "same-origin", body: new FormData(form), signal,
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error("Add not confirmed");
      return response.json();
    }, 8000);
    if (!added || String(added.id) !== id) throw new Error("Add not confirmed");
    if (status) status.textContent = "Added to bag.";
    if (cartLink) cartLink.hidden = false;
    try {
      const cart = await withRequestDeadline(async (signal) => {
        const cartResponse = await window.fetch(region.dataset.cartReadUrl, {
          credentials: "same-origin", cache: "no-store", signal, headers: { Accept: "application/json" },
        });
        return cartResponse.ok ? cartResponse.json() : null;
      }, 5000);
      if (cart) {
        if (Number.isInteger(cart.item_count) && cart.item_count >= 0) {
          document.querySelectorAll("[data-cart-count]").forEach((count) => { count.textContent = String(cart.item_count); });
        }
      }
    } catch {
    }
  } catch {
    if (status) status.textContent = "Check your bag before trying again. We could not confirm this add.";
    if (cartLink) cartLink.hidden = false;
  } finally {
    delete form.dataset.addPending;
    if (button) {
      const selected = form.closest("product-purchase")?.querySelector("[data-pdp-variant-select]")?.selectedOptions[0];
      button.disabled = selected ? selected.dataset.orderEligible !== "true" : false;
    }
  }
});

document.querySelectorAll("[data-contact-code]").forEach((input) => {
  if (input.value.trim()) return;
  const params = new URL(window.location.href).searchParams;
  const code = params.get("design_code") || params.get("product_code");
  if (code && code.length <= 40 && /^[A-Za-z0-9][A-Za-z0-9_-]*$/.test(code)) input.value = code;
});

document.addEventListener("click", (event) => {
  const link = event.target.closest(".mobile-menu a");
  if (link) link.closest("details")?.removeAttribute("open");
});
